import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TenantContext } from '../../context/TenantContext';
import './GradePicker.css';

// Per-tenant_type academic schema. Each tenant_type maps to:
//   streams        — Row 1 options (top-level Academic Stream)
//   levelsByStream — Row 2 options per stream (Year / Stage). May be `groups`
//                    (sub-headed buckets like Primary/Middle/High) or `options`
//                    (flat list).
//   cyclesByStream — Row 3 options per stream (Evaluation Cycle).
//
// Each option has { key, label }. `key` is the canonical persisted token used
// in `class_level` strings. `label` is the human-facing pill text.
const STREAM = {
  K12: 'K-12',
  UG: 'UG',
  PG: 'PG',
  PHD: 'Doctoral',
};

const K12_GROUPS = [
  {
    label: 'Primary (1-5)',
    options: [1, 2, 3, 4, 5].map(n => ({ key: `Class ${n}`, label: `Class ${n}` })),
  },
  {
    label: 'Middle School (6-8)',
    options: [6, 7, 8].map(n => ({ key: `Class ${n}`, label: `Class ${n}` })),
  },
  {
    label: 'High School (9-12)',
    options: [9, 10, 11, 12].map(n => ({ key: `Class ${n}`, label: `Class ${n}` })),
  },
];

const UG_YEARS = [
  { key: 'FY', label: 'First Year' },
  { key: 'SY', label: 'Second Year' },
  { key: 'TY', label: 'Third Year' },
  { key: 'Final', label: 'Final Year' },
];

const PG_YEARS = [
  { key: 'PG-Y1', label: 'First Year' },
  { key: 'PG-Y2', label: 'Second Year' },
];

const PHD_YEARS = [
  { key: 'PhD-Y1', label: 'Year 1' },
  { key: 'PhD-Y2', label: 'Year 2' },
  { key: 'PhD-Y3', label: 'Year 3+' },
];

const SEM_CYCLES = [
  { key: 'Sem I', label: 'Semester I' },
  { key: 'Sem II', label: 'Semester II' },
  { key: 'Sem III', label: 'Semester III' },
  { key: 'Sem IV', label: 'Semester IV' },
  { key: 'Annual', label: 'Annual' },
];

const K12_CYCLES = [
  { key: 'Term I', label: 'Term I' },
  { key: 'Term II', label: 'Term II' },
  { key: 'Annual', label: 'Annual' },
];

const SCHEMA = {
  school: {
    streams: [{ key: STREAM.K12, label: 'K-12' }],
    levelsByStream: { [STREAM.K12]: { groups: K12_GROUPS } },
    cyclesByStream: { [STREAM.K12]: K12_CYCLES },
  },
  university: {
    streams: [
      { key: STREAM.UG, label: 'Undergraduate' },
      { key: STREAM.PG, label: 'Postgraduate' },
    ],
    levelsByStream: {
      [STREAM.UG]: { options: UG_YEARS },
      [STREAM.PG]: { options: PG_YEARS },
    },
    cyclesByStream: {
      [STREAM.UG]: SEM_CYCLES,
      [STREAM.PG]: SEM_CYCLES,
    },
  },
  // college shares the university model
  college: null,
  enterprise: {
    streams: [
      { key: STREAM.K12, label: 'K-12' },
      { key: STREAM.UG, label: 'Undergraduate' },
      { key: STREAM.PG, label: 'Postgraduate' },
      { key: STREAM.PHD, label: 'Doctoral' },
    ],
    levelsByStream: {
      [STREAM.K12]: { groups: K12_GROUPS },
      [STREAM.UG]: { options: UG_YEARS },
      [STREAM.PG]: { options: PG_YEARS },
      [STREAM.PHD]: { options: PHD_YEARS },
    },
    cyclesByStream: {
      [STREAM.K12]: K12_CYCLES,
      [STREAM.UG]: SEM_CYCLES,
      [STREAM.PG]: SEM_CYCLES,
      [STREAM.PHD]: [{ key: 'Annual', label: 'Annual' }],
    },
  },
  // private shares the enterprise model
  private: null,
};
SCHEMA.college = SCHEMA.university;
SCHEMA.private = SCHEMA.enterprise;

// Fallback when tenant_type is unknown or not yet loaded
const DEFAULT_TENANT_TYPE = 'university';

function getSchema(tenantType) {
  return SCHEMA[tenantType] || SCHEMA[DEFAULT_TENANT_TYPE];
}

// Restrict a schema's selectable levels to a fixed allow-list of level keys
// (e.g. ['Class 11', 'Class 12']). Empty groups and streams that end up with
// no levels are dropped. If nothing matches, the schema is returned unchanged
// so the picker never renders an empty, unusable state.
function restrictSchemaLevels(schema, allowedLevelKeys) {
  if (!Array.isArray(allowedLevelKeys) || allowedLevelKeys.length === 0) return schema;
  const allow = new Set(allowedLevelKeys);

  const filterLevels = (levels) => {
    if (!levels) return levels;
    if (levels.options) return { options: levels.options.filter(o => allow.has(o.key)) };
    if (levels.groups) {
      return {
        groups: levels.groups
          .map(g => ({ ...g, options: g.options.filter(o => allow.has(o.key)) }))
          .filter(g => g.options.length > 0),
      };
    }
    return levels;
  };

  const levelsByStream = {};
  Object.keys(schema.levelsByStream).forEach(streamKey => {
    levelsByStream[streamKey] = filterLevels(schema.levelsByStream[streamKey]);
  });

  const keptStreams = schema.streams.filter(
    s => flattenLevelOptions(levelsByStream[s.key]).length > 0
  );
  if (keptStreams.length === 0) return schema;

  return { ...schema, streams: keptStreams, levelsByStream };
}

function flattenLevelOptions(levels) {
  if (!levels) return [];
  if (levels.options) return levels.options;
  if (levels.groups) return levels.groups.flatMap(g => g.options);
  return [];
}

// Encode a {stream, level, cycle} selection into the persisted class_level
// string. K-12 keeps the legacy "Class N" shape for back-compat with existing
// templates. Higher-ed combines tokens: e.g. "UG FY Sem I", "PG PG-Y2 Sem III".
function encodeClassLevel({ stream, level, cycle }) {
  if (!stream && !level) return '';
  if (stream === STREAM.K12 && level) {
    // level.key is already "Class N"; cycle is optional context.
    return cycle && cycle !== 'Annual' ? `${level} (${cycle})` : level;
  }
  const parts = [stream, level, cycle].filter(Boolean);
  return parts.join(' ');
}

// Best-effort decode of a persisted class_level back into a {stream, level,
// cycle} tuple against the active schema. Used to hydrate the picker from
// existing data.
function decodeClassLevel(classLevel, schema) {
  if (!classLevel) return { stream: '', level: '', cycle: '' };
  const text = String(classLevel).trim();

  // K-12 shape: "Class N" or "Class N (Term ...)"
  const k12Match = text.match(/^Class\s+\d+/i);
  if (k12Match) {
    const levelKey = k12Match[0].replace(/^Class\s+/i, 'Class ');
    const cycleMatch = text.match(/\(([^)]+)\)/);
    return {
      stream: STREAM.K12,
      level: levelKey,
      cycle: cycleMatch ? cycleMatch[1] : 'Annual',
    };
  }

  // Higher-ed shape: "<stream> <level> <cycle...>"
  const tokens = text.split(/\s+/);
  const streamKey = schema.streams.find(s => s.key === tokens[0])?.key || '';
  if (!streamKey) return { stream: '', level: '', cycle: '' };
  const levelOptions = flattenLevelOptions(schema.levelsByStream[streamKey]);
  const cycleOptions = schema.cyclesByStream?.[streamKey] || [];
  const levelKey = levelOptions.find(l => l.key === tokens[1])?.key || '';
  const cycleKey = cycleOptions.find(c => text.includes(c.key))?.key || '';
  return { stream: streamKey, level: levelKey, cycle: cycleKey };
}

function PillRow({ label, options, value, onChange, disabled }) {
  return (
    <div className="gp-row">
      <div className="gp-row-label">{label}</div>
      <div className="gp-pill-group">
        {options.map(opt => (
          <button
            key={opt.key}
            type="button"
            className={`gp-pill ${value === opt.key ? 'gp-pill-active' : ''}`}
            onClick={() => onChange(opt.key)}
            disabled={disabled}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function GroupedPillRow({ label, groups, value, onChange, disabled }) {
  return (
    <div className="gp-row">
      <div className="gp-row-label">{label}</div>
      <div className="gp-group-stack">
        {groups.map(group => (
          <div key={group.label} className="gp-subgroup">
            <div className="gp-subgroup-label">{group.label}</div>
            <div className="gp-pill-group">
              {group.options.map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  className={`gp-pill ${value === opt.key ? 'gp-pill-active' : ''}`}
                  onClick={() => onChange(opt.key)}
                  disabled={disabled}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="gp-row">
      <div className="gp-skeleton gp-skeleton-label" />
      <div className="gp-pill-group">
        <div className="gp-skeleton gp-skeleton-pill" />
        <div className="gp-skeleton gp-skeleton-pill" />
        <div className="gp-skeleton gp-skeleton-pill" />
        <div className="gp-skeleton gp-skeleton-pill" />
      </div>
    </div>
  );
}

/**
 * Universal multi-tier academic grade picker.
 *
 * Renders a 3-row segmented pill cascade (Stream → Year/Stage → Cycle) whose
 * structure adapts to the active tenant_type from TenantContext.
 *
 * Props:
 *   value           — current class_level string (e.g. "Class 11", "UG FY Sem I")
 *   onChange(value, meta) — called with the encoded string and {stream, level, cycle}
 *   compact         — render an inline 1-line summary that expands to the full
 *                     picker via popover (use inside per-question rows)
 *   allowClear      — show a small "Not specified" pill
 *   showBanner      — render the "Configuring Blueprint for: ..." footer
 *   tenantTypeOverride — force a tenant_type (mostly for testing / Storybook)
 *   onStreamLoadAsync — optional async hook fired when stream changes; while
 *                       its promise is pending the picker shows skeleton rows
 *                       (use this to pre-fetch syllabus blueprints).
 */
function GradePicker({
  value = '',
  onChange,
  compact = false,
  allowClear = true,
  showBanner = true,
  tenantTypeOverride,
  onStreamLoadAsync,
  allowedLevelKeys = null,
  className = '',
}) {
  const tenantCtx = useContext(TenantContext) || {};
  // Prefer the explicit `tenantType` string from context — the `tenant` object
  // is frequently null (the validate-tenant response only sets it when present),
  // which would otherwise silently fall back to the university schema.
  const tenantType = tenantTypeOverride
    || tenantCtx?.tenantType
    || tenantCtx?.tenant?.tenant_type
    || DEFAULT_TENANT_TYPE;

  const allowedLevelKey = Array.isArray(allowedLevelKeys) ? allowedLevelKeys.join('|') : '';
  const schema = useMemo(
    () => restrictSchemaLevels(getSchema(tenantType), allowedLevelKeys),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantType, allowedLevelKey]
  );

  // Hydrate selection from incoming `value` whenever it (or the schema) changes
  const initial = useMemo(() => decodeClassLevel(value, schema), [value, schema]);
  const [stream, setStream] = useState(initial.stream || schema.streams[0]?.key || '');
  const [level, setLevel] = useState(initial.level);
  const [cycle, setCycle] = useState(initial.cycle);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(!compact);
  // Fixed-position coordinates for the portaled popover (compact mode only).
  const [popoverStyle, setPopoverStyle] = useState(null);

  const popoverRef = useRef(null);
  const triggerRef = useRef(null);

  // Sync internal state with external value changes (controlled component)
  useEffect(() => {
    const next = decodeClassLevel(value, schema);
    setStream(next.stream || schema.streams[0]?.key || '');
    setLevel(next.level);
    setCycle(next.cycle);
  }, [value, schema]);

  // Close compact popover when clicking outside
  useEffect(() => {
    if (!compact || !expanded) return undefined;
    function handleClickOutside(e) {
      if (popoverRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setExpanded(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [compact, expanded]);

  // Position the portaled popover with fixed coordinates anchored to the
  // trigger. Rendering it at document.body (below) lets it escape any ancestor
  // with `overflow: hidden/auto` (scrollable card, modal, question list) that
  // would otherwise clip an absolutely-positioned dropdown. Re-measured on
  // scroll/resize so it stays glued to the trigger.
  useLayoutEffect(() => {
    if (!compact || !expanded) return undefined;
    const updatePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const gap = 4;
      const margin = 8;
      const vw = document.documentElement.clientWidth;
      const vh = document.documentElement.clientHeight;
      const width = Math.min(Math.max(rect.width, 320), 576, vw - margin * 2);
      let left = rect.left;
      if (left + width > vw - margin) left = Math.max(margin, vw - margin - width);
      const spaceBelow = vh - rect.bottom - gap - margin;
      const spaceAbove = rect.top - gap - margin;
      const placeAbove = spaceBelow < 240 && spaceAbove > spaceBelow;
      const style = {
        position: 'fixed',
        left: `${Math.round(left)}px`,
        width: `${Math.round(width)}px`,
        marginTop: 0,
        overflowY: 'auto',
      };
      if (placeAbove) {
        style.bottom = `${Math.round(vh - rect.top + gap)}px`;
        style.maxHeight = `${Math.round(spaceAbove)}px`;
      } else {
        style.top = `${Math.round(rect.bottom + gap)}px`;
        style.maxHeight = `${Math.round(spaceBelow)}px`;
      }
      setPopoverStyle(style);
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    // capture phase so we also catch scrolling inside nested overflow containers
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [compact, expanded]);

  const emit = (next) => {
    if (typeof onChange !== 'function') return;
    onChange(encodeClassLevel(next), next);
  };

  const handleStreamChange = async (nextStream) => {
    setStream(nextStream);
    setLevel('');
    setCycle('');
    emit({ stream: nextStream, level: '', cycle: '' });
    if (typeof onStreamLoadAsync === 'function') {
      setLoading(true);
      try {
        await onStreamLoadAsync(nextStream);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleLevelChange = (nextLevel) => {
    setLevel(nextLevel);
    emit({ stream, level: nextLevel, cycle });
  };

  const handleCycleChange = (nextCycle) => {
    setCycle(nextCycle);
    emit({ stream, level, cycle: nextCycle });
  };

  const handleClear = () => {
    setStream(schema.streams[0]?.key || '');
    setLevel('');
    setCycle('');
    emit({ stream: '', level: '', cycle: '' });
  };

  const activeLevels = schema.levelsByStream[stream];
  const activeCycles = schema.cyclesByStream?.[stream] || [];
  const streamLabel = schema.streams.find(s => s.key === stream)?.label || '';
  const levelLabel = flattenLevelOptions(activeLevels).find(l => l.key === level)?.label || '';
  const cycleLabel = activeCycles.find(c => c.key === cycle)?.label || '';

  const summaryParts = [streamLabel, levelLabel, cycleLabel].filter(Boolean);
  const summaryText = summaryParts.length > 0
    ? summaryParts.join(' / ')
    : 'Not specified';

  const panelBody = (
    <>
      {compact && (
        <div className="gp-panel-header">
          <span>Academic Grade</span>
          <button
            type="button"
            className="gp-panel-close"
            onClick={() => setExpanded(false)}
            aria-label="Close"
          >
            ×
          </button>
        </div>
      )}

      {/* Row 1: Stream */}
      {schema.streams.length > 1 ? (
        <PillRow
          label="Academic Stream"
          options={schema.streams}
          value={stream}
          onChange={handleStreamChange}
          disabled={loading}
        />
      ) : (
        <div className="gp-row gp-row-locked">
          <div className="gp-row-label">Academic Stream</div>
          <div className="gp-pill-group">
            <button type="button" className="gp-pill gp-pill-active" disabled>
              {schema.streams[0]?.label}
            </button>
          </div>
        </div>
      )}

      {/* Row 2: Year / Stage — skeleton while async stream fetch is running */}
      {loading ? (
        <SkeletonRow />
      ) : activeLevels?.groups ? (
        <GroupedPillRow
          label="Year / Stage"
          groups={activeLevels.groups}
          value={level}
          onChange={handleLevelChange}
        />
      ) : activeLevels?.options ? (
        <PillRow
          label="Year / Stage"
          options={activeLevels.options}
          value={level}
          onChange={handleLevelChange}
        />
      ) : null}

      {/* Row 3: Cycle */}
      {loading ? (
        <SkeletonRow />
      ) : activeCycles.length > 0 ? (
        <PillRow
          label="Evaluation Cycle"
          options={activeCycles}
          value={cycle}
          onChange={handleCycleChange}
        />
      ) : null}

      {/* Banner + clear */}
      {showBanner && (
        <div className="gp-banner-row">
          <div className="gp-banner">
            Configuring Blueprint for: <strong>{summaryText}</strong>
          </div>
          {allowClear && (stream || level || cycle) && (
            <button type="button" className="gp-clear" onClick={handleClear}>
              Clear
            </button>
          )}
        </div>
      )}
    </>
  );

  // Non-compact: render the full panel inline
  if (!compact) {
    return (
      <div className={`gp-panel ${className}`}>
        {panelBody}
      </div>
    );
  }

  // Compact: trigger pill + popover panel. The popover is portaled to
  // document.body with fixed positioning so it can never be clipped by a
  // scrollable/overflow-hidden ancestor (card, modal, question list).
  return (
    <span className={`gp-compact-wrap ${className}`}>
      <button
        type="button"
        ref={triggerRef}
        className={`gp-compact-trigger ${value ? 'gp-compact-trigger-filled' : ''}`}
        onClick={() => setExpanded(prev => !prev)}
        title="Click to change academic grade"
      >
        <span className="gp-compact-icon">🎓</span>
        <span className="gp-compact-text">{summaryText}</span>
        <span className="gp-compact-caret">▾</span>
      </button>
      {expanded && createPortal(
        <div
          ref={popoverRef}
          className="gp-panel gp-panel-popover"
          style={popoverStyle || { position: 'fixed', visibility: 'hidden' }}
        >
          {panelBody}
        </div>,
        document.body
      )}
    </span>
  );
}

export default GradePicker;
export { encodeClassLevel, decodeClassLevel, getSchema };
